// downloadFile.ts
// Cross-browser native download via hidden <iframe> + "download token cookie" signaling.
//
// Requirements on the server (Spring or any backend):
// 1) The download endpoint must return the file with:
//      Content-Disposition: attachment; filename="..."
// 2) The frontend will add a random token as a query param (default: downloadToken)
// 3) As soon as the server starts the response (headers/first bytes), it must set:
//      Set-Cookie: downloadToken=<token>; Path=/; SameSite=Lax
//    (Add Secure if HTTPS; consider HttpOnly=false so JS can read it)
//
// This lets the browser handle the download (shows in Transfers/Downloads UI)
// while JS learns "download started" by polling for the cookie value.

export type DownloadFileOptions = {
  /** Base URL for the download endpoint (relative or absolute). */
  url: string;

  /** Optional extra query parameters to add to the URL. */
  query?: Record<string, string | number | boolean | undefined | null>;

  /** Export file name with the name of the page being exported (ie. audit_trail). */
  filename: string;

  /** Query parameter name used to send the token to the server. */
  tokenParamName?: string; // default: "downloadToken"

  /** Cookie name the server sets back when the download starts. */
  cookieName?: string; // default: "downloadToken"

  /** Cookie name the server sets back when an error occurs in the download. */
  errorCookieName?: string; // default: "downloadErrorToken"

  /** How long to wait for the cookie signal. */
  timeoutMs?: number; // default: 60_000

  /** How often to poll document.cookie. */
  pollIntervalMs?: number; // default: 200

  /** Where to append the iframe. Defaults to document.body. */
  container?: HTMLElement;

  /**
   * Called once the cookie signal is observed (i.e., download started / first bytes likely sent).
   * Useful to turn loading OFF.
   */
  onStarted?: () => void;

  /**
   * Called if timeout occurs before we see the cookie.
   * You can turn loading OFF + show a warning.
   */
  onTimeout?: () => void;

  /**
   * Called if the browser blocks the iframe navigation (rare) or DOM fails.
   */
  onError?: (err: unknown) => void;

  /**
   * If true, removes iframe when finished. Default true.
   * (We keep it for a bit by default to avoid edge-case cancellation.)
   */
  cleanup?: boolean;
};

function randomToken(): string {
  // reasonably unique for this purpose
  return `${Date.now()}_${Math.random().toString(16).slice(2)}_${Math.random().toString(16).slice(2)}`;
}

function getCookie(name: string): string | null {
  // Safe cookie regex
  const escaped = name.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
  const m = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

function clearCookieBestEffort(names: string | string[]) {
  const cookieNames = Array.isArray(names) ? names : [names];

  cookieNames.forEach((name) => {
    // Best effort; path/domain must match how backend sets it.
    // We assume Path=/ (recommended) and no Domain attribute.
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
  });
}

function buildUrl(
  baseUrl: string,
  query: Record<string, unknown> | undefined,
  tokenParam: string,
  token: string,
): string {
  const url = new URL(baseUrl, window.location.origin);

  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      url.searchParams.set(key, String(value));
    });
  }

  url.searchParams.set(tokenParam, token);
  return url.toString();
}

/**
 * Starts a native browser download by navigating a hidden iframe to `url`.
 * Returns a Promise that resolves as soon as the backend signals "download started"
 * by setting a cookie whose value matches the generated token.
 *
 * Important: call this directly from a user gesture (e.g. button click) to avoid popup blockers.
 */
export async function downloadFile(opts: DownloadFileOptions): Promise<void> {
  const {
    url,
    query,
    filename,
    tokenParamName = 'downloadToken',
    cookieName = 'downloadToken',
    errorCookieName = 'downloadTokenError',
    container = document.body,
    onStarted,
    onError,
  } = opts;

  const token = randomToken();
  // Clear any previous stale cookie value (best effort).
  clearCookieBestEffort(cookieName);

  const finalUrl = buildUrl(url, query, tokenParamName, token);

  // Create a fetch request and trigger a hidden anchor to download blob
  try {
    // Fetch the download URL
    const response = await fetch(finalUrl, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      await Promise.reject(new Error(`Download failed with status ${response.status}`));
    }

    // Detect backend cookie error
    const errorToken = getCookie(errorCookieName);
    if (errorToken === token) {
      clearCookieBestEffort([cookieName, errorCookieName]);
      await Promise.reject(new Error('Download failed (error cookie received)'));
    }

    // Read the file
    const blob = await response.blob();

    // Create object URL
    const objectUrl = URL.createObjectURL(blob);

    // Trigger browser download
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.style.display = 'none';
    container.appendChild(a);
    a.click();
    a.remove();

    // Clear object URL once download starts
    URL.revokeObjectURL(objectUrl);

    onStarted?.();

    // Clear cookies after success
    clearCookieBestEffort([cookieName, errorCookieName]);
  } catch (err) {
    onError?.(err);
    throw err;
  }
}
